var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import React, { useState } from "react";
import { DragDropContext } from "react-beautiful-dnd";
import classNames from "classnames";
import PipelineService from "../../../Services/Pipeline.service";
import OpportunityService from "../../../Services/Opportunity.service";
import makeServicesCollector from "../../Hooks/makeServicesCollector";
import useErrorPopup from "../../Hooks/useErrorPopup";
import usePoll from "../../Hooks/usePoll";
import StageCard from "../../Presentational/StageCard/StageCard";
import { AlertInfo } from "../../UI/StandardAlerts/StandardAlerts";
const { Provider: p, register: servicesCollector, useServiceHook, } = makeServicesCollector();
export const Provider = p;
const pipelineMoveOpportunity = (dispatch, movingOpportunity, sourceStage, destinationStage) => {
    var _a;
    if (!destinationStage) {
        return;
    }
    if (destinationStage.id === (sourceStage === null || sourceStage === void 0 ? void 0 : sourceStage.id)) {
        // If we're moving to the same stage, don't do anything.
        return;
    }
    dispatch({
        type: "USE_SERVICE_ACTION",
        payload: {
            id: (_a = destinationStage.id) === null || _a === void 0 ? void 0 : _a.toString(),
            useServiceAction: {
                type: "EDIT_DATA",
                payload: (opportunityState) => {
                    var _a;
                    // set stage on moving opportunity to the destination stage, and add it to the beginning of the list.
                    // make sure the stage doesn't already exist in this stage.  If it does, don't do anything
                    if (opportunityState === null || opportunityState === void 0 ? void 0 : opportunityState.data.map((o) => o.id).includes(movingOpportunity.id)) {
                        // no change
                        return opportunityState;
                    }
                    const data = [
                        Object.assign(Object.assign({}, movingOpportunity), { stage: destinationStage, stageUpdatedDate: new Date() }),
                    ].concat((_a = opportunityState === null || opportunityState === void 0 ? void 0 : opportunityState.data) !== null && _a !== void 0 ? _a : []);
                    return Object.assign(Object.assign({}, opportunityState), { data, total: (opportunityState === null || opportunityState === void 0 ? void 0 : opportunityState.total) ? opportunityState.total + 1 : 1, offset: (opportunityState === null || opportunityState === void 0 ? void 0 : opportunityState.offset) ? opportunityState.offset + 1 : 1, maxResults: (opportunityState === null || opportunityState === void 0 ? void 0 : opportunityState.maxResults)
                            ? opportunityState.maxResults + 1
                            : 1 });
                },
            },
        },
    });
    // If there's a source, then we also need to remove this opportunity from the last stage.
    if (sourceStage) {
        dispatch({
            type: "USE_SERVICE_ACTION",
            payload: {
                id: sourceStage.id.toString(),
                useServiceAction: {
                    type: "EDIT_DATA",
                    payload: (opportunityState) => {
                        // Removing the opportunity.
                        // If the opportunity doesn't exist in this stage, then don't do anything
                        if (!(opportunityState === null || opportunityState === void 0 ? void 0 : opportunityState.data.map((o) => o.id).includes(movingOpportunity.id))) {
                            return opportunityState;
                        }
                        const data = opportunityState.data.filter((op) => op.id !== movingOpportunity.id);
                        return Object.assign(Object.assign({}, opportunityState), { data, total: opportunityState.total - 1 });
                    },
                },
            },
        });
    }
};
const PipelineCardView = ({ pipeline, className, opportunitySearch, isArchived, }) => {
    var _a;
    const { global: { data: globalData }, dispatch: opportunitiesDispatch, } = useServiceHook();
    const [isPipelineOutOfSync, setIsPipelineOutOfSync] = React.useState(false);
    const allOpportunities = 
    // @ts-expect-error - TS2339 - Property 'flatMap' does not exist on type 'undefined[] | PagedResult<Opportunity[]>'. | TS7006 - Parameter 'state' implicitly has an 'any' type.
    (_a = globalData === null || globalData === void 0 ? void 0 : globalData.flatMap((state) => { var _a; return (_a = state === null || state === void 0 ? void 0 : state.data) !== null && _a !== void 0 ? _a : []; })) !== null && _a !== void 0 ? _a : [];
    const [popupError, errorModals] = useErrorPopup();
    const [lastUpdatedStageWatermark, setLastUpdatedStageWatermark] = useState(pipeline.maxOpportunityStageUpdatedDate);
    const poll = ({ stopPoll }) => __awaiter(void 0, void 0, void 0, function* () {
        const newWatermark = yield PipelineService.getLastOpportunityStageMoveDate(pipeline.id);
        if ((newWatermark === null || newWatermark === void 0 ? void 0 : newWatermark.getTime()) !== (lastUpdatedStageWatermark === null || lastUpdatedStageWatermark === void 0 ? void 0 : lastUpdatedStageWatermark.getTime())) {
            setLastUpdatedStageWatermark(newWatermark);
            // Get new opportunities and integrate them into the board
            const resp = yield OpportunityService.searchLite(Object.assign(Object.assign({}, opportunitySearch), { pipelineIds: [pipeline.id], stageUpdatedDateFrom: lastUpdatedStageWatermark }), { max: 100, sort: "OPPORTUNITY_PIPELINE_ENTRY_DATE" });
            if (resp.total > 100) {
                // There are too many opportunities to load in.
                // Show a message that the pipeline is out of sync and stop the poll.
                setIsPipelineOutOfSync(true);
                stopPoll();
            }
            const receivedOpportunities = resp.data;
            const allOpportunityIds = allOpportunities.map((o) => o.id);
            const changedOpportunities = [];
            const newOpportunities = [];
            receivedOpportunities.forEach((o) => {
                if (allOpportunityIds.includes(o.id)) {
                    changedOpportunities.push(o);
                }
                else {
                    newOpportunities.push(o);
                }
            });
            // add new opportunities
            newOpportunities.forEach((o) => {
                pipelineMoveOpportunity(opportunitiesDispatch, o, null, o.stage);
            });
            // move old opportunities
            changedOpportunities.forEach((o) => {
                const existingOpportunity = allOpportunities.find((eo) => eo.id === o.id);
                // This should never happen, but check to make flow happy.
                if (!existingOpportunity) {
                    return;
                }
                pipelineMoveOpportunity(opportunitiesDispatch, o, existingOpportunity.stage, o.stage);
            });
        }
    });
    usePoll(poll, { timeMs: 5000 });
    const [stageOpportunityIsMovingTo, setStageOpportunityIsMovingTo] = useState(null);
    const [movingOpportunity, setMovingOpportunity] = useState(null);
    return (_jsxs("div", { children: [isPipelineOutOfSync && (_jsx(AlertInfo, { message: _jsxs("span", { className: "d-flex align-items-center", children: ["This pipeline is out of sync.", " ", _jsx("button", { className: "btn btn-link alert-link mx-1 p-0", onClick: () => window.location.reload(), children: "Reload the page" }), " ", "to get the latest updates."] }) })), _jsxs(DragDropContext, { onDragEnd: ({ destination, source, draggableId }) => {
                    if (!destination) {
                        return;
                    }
                    if (source.droppableId === destination.droppableId) {
                        return;
                    }
                    const opportunityToMove = allOpportunities === null || allOpportunities === void 0 ? void 0 : allOpportunities.find((o) => o.id.toString() === draggableId);
                    if (!opportunityToMove) {
                        return;
                    }
                    // we always want to move the opportunity to the first spot in the stage
                    // find the appropriate stage
                    const destinationStage = pipeline.stages.find((s) => s.id.toString() === destination.droppableId);
                    if (!destinationStage) {
                        return;
                    }
                    const startStage = opportunityToMove.stage;
                    setStageOpportunityIsMovingTo(destinationStage.id);
                    setMovingOpportunity(opportunityToMove);
                    OpportunityService.moveOpportunity(opportunityToMove.id, destinationStage.id)
                        .then((op) => {
                        if (op) {
                            pipelineMoveOpportunity(opportunitiesDispatch, op, startStage, destinationStage);
                        }
                    })
                        .catch((reason) => {
                        popupError(reason);
                    })
                        .finally(() => {
                        setStageOpportunityIsMovingTo(null);
                        setMovingOpportunity(null);
                    });
                }, children: [_jsx("div", { className: "d-flex text-nowrap ml-1 pb-3", style: {
                            minHeight: "100%",
                        }, children: pipeline.stages
                            .filter((stage) => {
                            var _a;
                            return !opportunitySearch.stageIds ||
                                opportunitySearch.stageIds.length === 0 ||
                                ((_a = opportunitySearch.stageIds) === null || _a === void 0 ? void 0 : _a.includes(stage.id));
                        })
                            .map((stage) => (_jsx("div", { className: "px-1 w-100", style: { minWidth: 315, verticalAlign: "top" }, children: _jsx(StageCard, { stage: stage, className: classNames("flex-grow-1"), opportunitySearch: opportunitySearch, opportunities: allOpportunities.filter((o) => o.stage.id === stage.id && (movingOpportunity === null || movingOpportunity === void 0 ? void 0 : movingOpportunity.id) !== o.id), opportunityMovingToThisStage: stageOpportunityIsMovingTo === stage.id && movingOpportunity
                                    ? movingOpportunity
                                    : null, pipeline: pipeline, dragDisabled: isArchived || movingOpportunity != null, 
                                // @ts-expect-error - TS2322 - Type 'ServiceCollectorRegister<PagedResult<Opportunity[]>>' is not assignable to type 'Context<MakeServicesCollectorContextState<Opportunity[]>>'.
                                serviceCollector: servicesCollector }) }, stage.id))) }), errorModals] })] }));
};
export default PipelineCardView;
